/**
 * GameLifecycleManager
 *
 * Manages Playjector host mode state, game lifecycle, and Steam login monitoring.
 *
 * Responsibilities:
 * - Track game launch/close state
 * - Monitor Steam user login
 * - Handle logout timeouts
 * - Update game information for matchmaking
 * - Handle Ad Mode game restart logic
 * - Send analytics for game lifecycle events
 *
 * Source: apps/host/host-frontend/src/api/modules/events/playjectorHostModeEvents.ts
 */

import type { GameLaunchStores } from '../stores';
import type { GameLaunchModeState } from '../GameLaunchCoordinator';
import type { CoordinatorEvent } from '../../_types/events';
import type { AppCoordinator } from '../../app/AppCoordinator';
import type { SessionCoordinator } from '../../session/SessionCoordinator';
import type { IHostAnalyticsManager } from '../../../services/analytics/IHostAnalyticsManager';

interface GameLaunchEvents {
  gameLaunched: CoordinatorEvent<{ gameId: number; gameName: string }>;
  gameClosed: CoordinatorEvent<{ gameId: number; gameName: string }>;
  steamUserLoggedOut: CoordinatorEvent<void>;
  logoutTimeoutExpired: CoordinatorEvent<void>;
  gameInfoUpdated: CoordinatorEvent<{ gameId: number; gameName: string }>;
}

export class GameLifecycleManager {
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private lastGameName: string | null = null;
  private lastGameId: number | null = null;
  private lastGameLaunched = false;
  private pendingGameLaunchedAnalytics = false;
  private sessionCoordinator?: SessionCoordinator;

  constructor(
    private stores: GameLaunchStores,
    private events: GameLaunchEvents,
    private readonly appCoordinator: AppCoordinator,
    private readonly analytics: IHostAnalyticsManager,
  ) {}

  /**
   * Initialize the manager
   * Sets up listeners for Playjector state changes
   */
  init(sessionCoordinator?: SessionCoordinator): void {
    console.log('[GameLifecycleManager] Initialized');

    this.sessionCoordinator = sessionCoordinator;

    // Listen to playjector state changes
    this.stores.gameMode.addListener((gameLaunchState) => {
      this.handlePlayjectorStateChange(gameLaunchState);
    });
  }

  /**
   * Handle Playjector state changes
   */
  private handlePlayjectorStateChange(gameLaunchState: GameLaunchModeState): void {
    const isStreaming = this.sessionCoordinator?.getStatus() === 'hosting';
    const isPavilionMode = this.appCoordinator.getEdition() === 'pavilion';

    // Track game launch state changes
    if (gameLaunchState.gameLaunched !== this.lastGameLaunched) {
      if (gameLaunchState.gameLaunched) {
        this.handleGameLaunched(gameLaunchState);
      } else {
        this.handleGameClosed(gameLaunchState);
      }
      this.lastGameLaunched = gameLaunchState.gameLaunched;
    }

    // Fire deferred Game Launched analytics once Playjector populates game identity.
    // Playjector may send gameLaunched:true before gameId/gameName are known, so we
    // wait for a subsequent state update where both fields are valid.
    if (this.pendingGameLaunchedAnalytics && gameLaunchState.gameId !== 0 && gameLaunchState.gameName !== '') {
      this.pendingGameLaunchedAnalytics = false;
      this.lastGameId = gameLaunchState.gameId;
      this.lastGameName = gameLaunchState.gameName;
      this.analytics.sendGameLaunchedEvent({
        gameId: gameLaunchState.gameId,
        gameName: gameLaunchState.gameName,
      });
    }

    // Handle Steam game information updates (for matchmaking)
    if (isStreaming && isPavilionMode && this.stores.matchmaker.currentState.stateDetails?.currentMatchInvitationId) {
      this.handleGameInfoUpdate(gameLaunchState);
    }

    // Monitor Steam login state
    if (isStreaming && isPavilionMode) {
      this.handleSteamLoginMonitoring(gameLaunchState);
    } else if (this.logoutTimer) {
      // Clear timer if not streaming or not in pavilion mode
      this.clearLogoutTimer();
    }
  }

  /**
   * Handle game launched event
   */
  private handleGameLaunched(gameLaunchState: GameLaunchModeState): void {
    console.log('[GameLifecycleManager] Game Launched:', {
      gameName: gameLaunchState.gameName,
      gameId: gameLaunchState.gameId,
    });

    // Track game info for gameClosed event
    this.lastGameId = gameLaunchState.gameId;
    this.lastGameName = gameLaunchState.gameName;

    // Emit event
    this.events.gameLaunched.emit({
      gameId: gameLaunchState.gameId,
      gameName: gameLaunchState.gameName,
    });

    // Playjector may send gameLaunched:true before it knows which game is running
    // (gameId:0 / gameName:''). Fire immediately if identity is already populated,
    // otherwise set a flag and fire from handlePlayjectorStateChange once it arrives.
    if (gameLaunchState.gameId !== 0 && gameLaunchState.gameName !== '') {
      this.analytics.sendGameLaunchedEvent({
        gameId: gameLaunchState.gameId,
        gameName: gameLaunchState.gameName,
      });
    } else {
      this.pendingGameLaunchedAnalytics = true;
    }
  }

  /**
   * Handle game closed event
   */
  private handleGameClosed(gameLaunchState: GameLaunchModeState): void {
    console.log('[GameLifecycleManager] Game Closed:', {
      previousGameName: this.lastGameName,
      previousGameId: this.lastGameId,
    });

    // Cancel any pending deferred analytics — game closed before identity arrived
    this.pendingGameLaunchedAnalytics = false;

    // Emit event
    this.events.gameClosed.emit({
      gameId: this.lastGameId || 0,
      gameName: this.lastGameName || '',
    });

    this.analytics.sendGameClosedEvent({
      gameId: this.lastGameId || 0,
      gameName: this.lastGameName || '',
    });

    // Handle Ad Mode-specific logic
    if (this.appCoordinator.getEdition() === 'pavilion' && this.stores.adMode.currentState.enabled) {
      this.handleAdModeGameClosed();
    }

    // TODO: Send to Sentry as breadcrumb
  }

  /**
   * Handle Ad Mode game closed logic
   *
   * TODO-ARCHITECTURE: This should emit events instead of modifying matchmaking state!
   * PavilionCoordinator should listen to events and update its own state.
   */
  private handleAdModeGameClosed(): void {
    // TODO-ARCHITECTURE: Emit event instead of handling directly.
    // PavilionCoordinator / SessionCoordinator should decide whether to restart.
    console.log('[GameLifecycleManager] Ad Mode: Game closed, awaiting restart');
  }

  /**
   * Handle game information updates (for matchmaking invitation)
   */
  private handleGameInfoUpdate(gameLaunchState: GameLaunchModeState): void {
    const gameNameChanged = gameLaunchState.gameName !== this.lastGameName;
    const gameIdChanged = gameLaunchState.gameId !== this.lastGameId;

    if (gameNameChanged || gameIdChanged) {
      console.log('[GameLifecycleManager] Steam game information changed:', {
        gameName: gameLaunchState.gameName,
        gameId: gameLaunchState.gameId,
      });

      // TODO: Send analytics
      // if (gameLaunchState.gameName !== '') {
      //   sendAnalytics({ name: 'Game Launched', ... });
      // }

      // TODO: Update invitation with new Steam game information
      // sendMessage(
      //   websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION, {
      //     invitationId: matchmakerState.currentState.stateDetails.currentMatchInvitationId,
      //     name: gameLaunchState.gameName,
      //     steamId: gameLaunchState.gameId.toString(),
      //   }),
      // );

      this.events.gameInfoUpdated.emit({
        gameId: gameLaunchState.gameId,
        gameName: gameLaunchState.gameName,
      });

      // Update cached values
      this.lastGameName = gameLaunchState.gameName;
      this.lastGameId = gameLaunchState.gameId;
    }
  }

  /**
   * Monitor Steam login state and handle logout timeout
   */
  private handleSteamLoginMonitoring(gameLaunchState: GameLaunchModeState): void {
    if (gameLaunchState.userLoggedIn) {
      // User logged in - clear any existing timer
      if (this.logoutTimer) {
        this.clearLogoutTimer();
      }
    } else if (!gameLaunchState.userLoggedIn && !this.logoutTimer) {
      // User logged out - start timeout timer
      console.log('[GameLifecycleManager] User logged out, starting 10 minute timer before stopping stream');

      // TODO: Uncomment when ready
      // this.logoutTimer = setTimeout(() => {
      //   console.log('[GameLifecycleManager] User has been logged out for 10 minutes, stopping stream');
      //   this.events.logoutTimeoutExpired.emit();
      //   // TODO: Call IPC to stop stream
      //   // stopStream('playjector:host_mode:steam_user_logged_out');
      //   this.logoutTimer = null;
      // }, config.PAVILION_STEAM_LOGOUT_TIMEOUT);

      this.events.steamUserLoggedOut.emit();
    }
  }

  /**
   * Clear logout timer
   */
  private clearLogoutTimer(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
      console.log('[GameLifecycleManager] Cleared logout timer');
    }
  }

  /**
   * Cleanup on manager destruction
   */
  destroy(): void {
    this.clearLogoutTimer();
  }
}
